import { z } from 'zod'

/**
 * The canonical application identity is the normalized email address itself.
 *
 * Normalization is intentionally minimal: trim surrounding whitespace and lowercase
 * the complete address. Provider specific tricks (removing Gmail dots, stripping
 * "+tag" suffixes) are deliberately NOT applied, so `john+work@gmail.com` and
 * `john@gmail.com` stay two distinct application identities.
 */
export function normalizeEmailAddress(rawEmailAddress: string): string {
  return rawEmailAddress.trim().toLowerCase()
}

export const MAXIMUM_EMAIL_ADDRESS_LENGTH = 320

/** Accepts any reasonably shaped address and yields its normalized form. */
export const normalizedEmailAddressSchema = z
  .string()
  .max(MAXIMUM_EMAIL_ADDRESS_LENGTH)
  .transform(normalizeEmailAddress)
  .pipe(z.email())

export function isValidEmailAddress(candidateEmailAddress: string): boolean {
  return normalizedEmailAddressSchema.safeParse(candidateEmailAddress).success
}

/**
 * Display fallback used when a placeholder account has no display name yet.
 * The local part of the address reads better than the full address in dense UI.
 */
export function describeEmailAddressLocalPart(emailAddress: string): string {
  const separatorPosition = emailAddress.lastIndexOf('@')
  return separatorPosition > 0 ? emailAddress.slice(0, separatorPosition) : emailAddress
}
